// list of pending new onboarded accounts for verification
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { usePendingAccount } from '../../context/PendingAccountContext';  
import { getAccountStatusColor } from '../../utils/helper';
import PendingAccountRemarks from './PendingAccountRemarks';
import CustomerContact from '../../components/common/CustomerContact';

const PendingAccounts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [actionType, setActionType] = useState('');
  const [openRemarksDialog, setOpenRemarksDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { fetchPendingAccounts, accountVerification } = usePendingAccount(); 
  const [pendingAccounts, setPendingAccounts] = useState([]);

  useEffect(() => {
    const fetchAccounts = async () => {
      const data = await fetchPendingAccounts();
      setPendingAccounts(data);
    }
    fetchAccounts();
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Filter accounts based on search term and status filter
  const filteredAccounts = pendingAccounts.filter(account => {
    const searchLower = searchTerm.toLowerCase();
    const statusLower = statusFilter.toLowerCase();
    
    // Search in company_name, contact.name, contact.email, contact.mobile
    const matchesSearch = 
      account.company_name?.toLowerCase().includes(searchLower) ||
      account.contact?.name?.toLowerCase().includes(searchLower) ||
      account.contact?.email?.toLowerCase().includes(searchLower) ||
      account.contact?.mobile?.toLowerCase().includes(searchLower);
    
    // Filter by status
    const matchesStatus = 
      statusFilter === 'all' || 
      account.status?.toLowerCase() === statusLower ||
      account.status_verbose?.toLowerCase().includes(statusLower);
    
    return matchesSearch && matchesStatus;
  });

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleAccountAction = async (account, action) => {
    setSelectedAccount(account);
    setActionType(action);
    setOpenRemarksDialog(true);
  };

  const handleVerification = async (account_id, remarks, status, customData = {}) => {
    try {
      const response = await accountVerification(account_id, remarks, status, customData);
      setOpenRemarksDialog(false);
      
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
        // Refresh the table with new data
        const updatedData = await fetchPendingAccounts();
        setPendingAccounts(updatedData || []);
      } else {
        setSnackbar({
          open: true,
          message: response.data.details,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Error processing verification',
        severity: 'error'
      });
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Pending Account Verification
        </Typography>
      </Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by company name, contact name, or email..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilter}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="on_hold">On Hold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {/* Results counter */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredAccounts.length} of {pendingAccounts.length} accounts
              {searchTerm && ` matching "${searchTerm}"`}
            </Typography>
            {filteredAccounts.length === 0 && pendingAccounts.length > 0 && (
              <Typography variant="body2" color="warning.main">
                No accounts found matching your search criteria
              </Typography>
            )}
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {account.company_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <CustomerContact contact={account.contact} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={account.status_verbose}
                        color={getAccountStatusColor(account.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {account.created_at}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {account.pending_since}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                          
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleAccountAction(account, 'AP')}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            {account.status !== 'RJ' && (
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleAccountAction(account, 'RJ')}
                                >
                                  <RejectIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {account.status !== 'HO' && (
                            <Tooltip title="Put on Hold">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleAccountAction(account, 'HO')}
                              >
                                <PauseCircleOutlineIcon />
                              </IconButton>
                            </Tooltip>
                            )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <PendingAccountRemarks
        open={openRemarksDialog}
        onClose={() => setOpenRemarksDialog(false)}
        account={selectedAccount}
        actionType={actionType}
        onVerification={handleVerification}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingAccounts;